import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { ActivityFormData } from "../../detail/interface";
import VuiSelect from "../../../../components/select";
import ConstantRepository from "../../../../repositories/constant-repository";
import { ReactNode, useMemo } from "react";
import {
  filteredLeadStatusOptions,
  leadActivityCloseLostReasonOptions,
  leadStatusOptions,
} from "../../../../utils/helpers";
import VuiDatePicker from "../../../../components/vui-date-picker";
import moment from "moment";
import UserRepository from "../../../../repositories/user-repository";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import VuiUploadImage from "../../../../components/upload-image";
import { User } from "../../../../models/user";
import { convertToSelectValue } from "../../../../components/select/function";

export type ModalFormActivityProps = {
  show: boolean;
  onClose: () => void;
  submitLoading: boolean;
  onSubmit: (formData: ActivityFormData) => void;
  title: string | ReactNode;
  availableStatuses: string[];
  assignee?: User;
};

const leadActivityParticipantDefaultValue = {
  name: null,
  email: null,
  whatsapp: null,
};

const ModalFormActivity = ({
  show,
  onClose,
  submitLoading,
  onSubmit,
  title,
  availableStatuses,
  assignee,
}: ModalFormActivityProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const defaultStatusValue = useMemo(() => {
    const defaultStatus =
      availableStatuses.length > 0
        ? leadStatusOptions(t).find((item) => item.id === availableStatuses[0])
        : undefined;

    return defaultStatus ? convertToSelectValue(defaultStatus) : undefined;
  }, [t, availableStatuses]);

  const watchStatus = Form.useWatch("status", form);
  const currentEmail = Form.useWatch(["decision_make", 0, "email"], form);
  const currentPhone = Form.useWatch(["decision_make", 0, "phone"], form);

  const statusTooltipKeys: Record<string, string> = {
    QUALIFIED_OPPORTUNITY: "common.form.updateStatusTo.tooltip.qualifiedOpportunity",
    SOLUTION_FIT: "common.form.updateStatusTo.tooltip.solutionFit",
    VENDOR_OF_CHOICE: "common.form.updateStatusTo.tooltip.vendorOfChoice",
    TERMS_AGREED: "common.form.updateStatusTo.tooltip.termsAgreed",
    CLOSED_WON: "common.form.updateStatusTo.tooltip.closedWon",
    CLOSED_LOST: "common.form.updateStatusTo.tooltip.closedLost",
  };

  const combinedStatusTooltip = useMemo(() => {
    if (!availableStatuses || availableStatuses.length === 0) return null;

    const nodes = availableStatuses.map((s) => {
      const key = statusTooltipKeys[s] || "common.form.updateStatusTo.tooltip";
      return (
        <div
          key={s}
          dangerouslySetInnerHTML={{ __html: t(key, { status: s }) }}
          className="lead-activity-status-tooltip-item"
        />
      );
    });

    return <div>{nodes}</div>;
  }, [availableStatuses, statusTooltipKeys, t]);

  return (
    <Modal
      title={title}
      width={768}
      centered
      open={show}
      onCancel={onClose}
      footer={[
        <Button key="cancel-button" onClick={onClose}>
          {t("common.button.cancel")}
        </Button>,
        <Button
          key="submit-button"
          type="primary"
          loading={submitLoading}
          onClick={() => {
            form
              .validateFields()
              .then((values: ActivityFormData) => {
                onSubmit(values);
                form.resetFields();
              })
              .catch((info: string) => {
                console.log("Validate Failed:", info);
              });
          }}
        >
          {t("common.button.submit")}
        </Button>,
      ]}
    >
      <Form
        form={form}
        initialValues={{
          status: defaultStatusValue,
          user_id: convertToSelectValue(assignee ?? null),
          date: moment(),
          contact_date: moment(),
          meeting_date: moment(),
          proposal_estimate_date: moment(),
          proposal_sent_date: moment(),
        }}
        layout={"vertical"}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} md={8}>
            <Form.Item
              name="status"
              label={
                <Space size={4}>
                  {t("common.form.updateStatusTo.label")}
                  <Tooltip title={combinedStatusTooltip}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              rules={[
                {
                  required: true,
                  message: t("validation.required", {
                    item: t("common.form.updateStatusTo.label"),
                  }),
                },
              ]}
            >
              <VuiSelect
                allowClear={false}
                defaultOptions={filteredLeadStatusOptions(t, availableStatuses)}
                placeholder={t("common.text.updateStatusTo")}
              />
            </Form.Item>
          </Col>
          {watchStatus?.value === "QUALIFIED_OPPORTUNITY" && (
            <>
              <Col xs={24} md={8}>
                <Form.Item
                  name="timeline"
                  label={
                    <Space size={4}>
                      {t("common.form.timeline.label")}
                      <Tooltip title={t("common.form.timeline.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.timeline.label"),
                      }),
                    },
                  ]}
                >
                  <VuiSelect
                    repository={ConstantRepository}
                    repositoryParams={{
                      for: "timeline_lead_activity",
                    }}
                    labelKey="label"
                    placeholder={t("common.form.timeline.placeholder")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="estimated_close_date"
                  label={t("common.form.estimatedCloseDate.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.estimatedCloseDate.label"),
                      }),
                    },
                  ]}
                >
                  <VuiDatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="pain_point"
                  label={t("common.form.painPoint.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.painPoint.field"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t(
                      "common.form.painPoint.placeholder"
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="need"
                  label={t("common.form.need.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.need.field"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t(
                      "common.form.need.placeholder"
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="authority"
                  label={
                    <Space size={4}>
                      {t("common.form.authority.label")}
                      <Tooltip title={t("common.form.authority.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.authority.label"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t(
                      "common.form.authority.placeholder"
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="qualified_opportunity_internal_note"
                  label={t("common.form.internalNote.label")}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t(
                      "common.form.internalNote.placeholder"
                    )}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {watchStatus?.value === "SOLUTION_FIT" && (
            <>
              <Col xs={0} md={8} />
              <Col xs={0} md={8} />
              <Col xs={24} md={12}>
                <Form.Item
                  name="metric"
                  label={
                    <Space size={4}>
                      {t("common.form.metric.label")}
                      <Tooltip title={t("common.form.metric.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.metric.label"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.metric.placeholder")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="decision"
                  label={
                    <Space size={4}>
                      {t("common.form.decision.label")}
                      <Tooltip title={t("common.form.decision.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.decision.label"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.decision.placeholder")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="solution_fit_internal_note"
                  label={t("common.form.internalNote.label")}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.internalNote.placeholder")}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {watchStatus?.value === "VENDOR_OF_CHOICE" && (
            <>
              <Col xs={24} md={8}>
                <Form.Item
                  name="internal_influence_id"
                  label={
                    <Space size={4}>
                      {t("common.form.internalInfluence.label")}
                      <Tooltip title={t("common.form.internalInfluence.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.internalInfluence.label"),
                      }),
                    },
                  ]}
                >
                  <VuiSelect
                    repository={UserRepository}
                    placeholder={t("common.form.internalInfluence.placeholder")}
                  />
                </Form.Item>
              </Col>
              <Col xs={0} md={8} />

              <Col xs={24}>
                <Card
                  title={
                    <Space size={4}>
                      {t("common.form.decisionMaker.label")}
                      <Tooltip title={t("common.form.decisionMaker.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                >
                  <Form.List name={["decision_make"]} initialValue={[{}]}>
                    {(fields) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Row key={key} gutter={[16, 8]}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, "full_name"]}
                                label={t("common.form.fullName.label")}
                                rules={[
                                  {
                                    required: true,
                                    message: t("validation.required", {
                                      item: t("common.form.fullName.label"),
                                    }),
                                  },
                                ]}
                              >
                                <Input size="large" placeholder={t("common.form.fullName.placeholder")} />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, "job_title"]}
                                label={t("common.form.jobTitle.label")}
                                rules={[
                                  {
                                    required: true,
                                    message: t("validation.required", {
                                      item: t("common.form.jobTitle.label"),
                                    }),
                                  },
                                ]}
                              >
                                <Input size="large" placeholder={t("common.form.jobTitle.placeholder")} />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, "email"]}
                                label={t("common.form.email.label")}
                                rules={[
                                  {
                                    required: (currentPhone?.length ?? 0) === 0,
                                    message: t("validation.required", {
                                      item: t("common.form.email.label"),
                                    }),
                                  },
                                  {
                                    type: "email",
                                    message: t("validation.invalid", {
                                      item: t("common.form.email.label"),
                                    }),
                                  }
                                ]}
                              >
                                <Input
                                  size="large" placeholder={t("common.form.email.placeholder")} />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                {...restField}
                                name={[name, "phone"]}
                                label={t("common.form.phoneNumber.label")}
                                rules={[
                                  {
                                    required: (currentEmail?.length ?? 0) === 0,
                                    message: t("validation.required", {
                                      item: t("common.form.phoneNumber.label"),
                                    }),
                                  }
                                ]}
                              >
                                <Input size="large" placeholder={t("common.form.phoneNumber.placeholder")} />
                              </Form.Item>
                            </Col>
                          </Row>
                        ))}
                      </>
                    )}
                  </Form.List>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="vendor_of_choice_internal_note"
                  label={t("common.form.internalNote.label")}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.internalNote.placeholder")}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {watchStatus?.value === "TERMS_AGREED" && (
            <>
              <Col xs={24} md={16}>
                <Form.Item
                  name="proposal_attachment_id"
                  label={
                    <Space size={4}>
                      {t("common.form.proposalAttachment.label")}
                      <Tooltip title={t("common.form.proposalAttachment.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.proposalAttachment.label"),
                      }),
                    },
                  ]}
                >
                  <VuiUploadImage
                    accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx"
                    buttonSize="large"
                    maxFileSizeMB={10}
                    listType="text"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="decision_process"
                  label={
                    <Space size={4}>
                      {t("common.form.decisionProcess.label")}
                      <Tooltip title={t("common.form.decisionProcess.tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.decisionProcess.label"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.decisionProcess.placeholder")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="terms_agreed_note"
                  label={t("common.form.note.label")}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.note.placeholder")}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {watchStatus?.value === "CLOSED_WON" && (
            <>
              <Col xs={24} md={16}>
                <Form.Item
                  name="contract_attachment_id"
                  label={t("common.form.contractAttachment.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.contractAttachment.label"),
                      }),
                    },
                  ]}
                >
                  <VuiUploadImage
                    accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx"
                    buttonSize="large"
                    maxFileSizeMB={10}
                    listType="text"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="closed_won_note"
                  label={t("common.form.note.label")}
                >
                  <Input.TextArea
                    rows={4}
                    size="large"
                    placeholder={t("common.form.note.placeholder")}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {watchStatus?.value === "CLOSED_LOST" && (
            <>
              <Col xs={24} md={8}>
                <Form.Item
                  name="lost_reason"
                  label={t("common.form.lostReason.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.lostReason.label"),
                      }),
                    },
                  ]}
                >
                  <VuiSelect
                    placeholder={t("common.form.lostReason.placeholder")}
                    defaultOptions={leadActivityCloseLostReasonOptions(t)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="lost_analysis"
                  label={t("common.form.lostAnalysis.label")}
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: t("common.form.lostAnalysis.field"),
                      }),
                    },
                  ]}
                >
                  <Input.TextArea
                    size="large"
                    placeholder={t("common.form.lostAnalysis.placeholder")}
                    rows={4}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalFormActivity;
